import React, { useState, useEffect, useCallback } from 'react';
import {
    Palette,
    Type,
    Globe,
    Share2,
    Layout,
    Save,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Eye,
    Facebook,
    Instagram,
    Phone,
    Search,
    ShoppingCart,
    ToggleLeft,
    ToggleRight,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPut } from '../../services/storefrontApi';
import AppleSelect from '../Common/AppleSelect';

/* ─── colour scheme palette ────────────────────────────────────────────── */
const SCHEME_LABELS = {
    default:     'Défaut',
    ocean:       'Océan',
    sunset:      'Coucher de soleil',
    royal:       'Royal',
    forest:      'Forêt',
    midnight:    'Minuit',
    coral:       'Corail',
    golden:      'Doré',
    monochrome:  'Monochrome',
    custom:      'Personnalisé',
};

const ColorDot = ({ color }) => (
    <span
        className="inline-block w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
        style={{ backgroundColor: color }}
    />
);

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden mb-4">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <div className="flex items-center gap-2 font-semibold text-sm text-gray-700 dark:text-gray-200">
                    <Icon size={16} className="text-primary" />
                    {title}
                </div>
                {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>
            {open && <div className="px-5 py-5">{children}</div>}
        </div>
    );
};

const FieldRow = ({ label, children }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <label className="text-sm text-gray-500 dark:text-gray-400 sm:w-44 flex-shrink-0">{label}</label>
        <div className="flex-1">{children}</div>
    </div>
);

const Toggle = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
        <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
);

/* ─── component ─────────────────────────────────────────────────────────── */
const ThemeSettings = () => {
    const [config, setConfig] = useState(null);
    const [schemes, setSchemes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const [cfgRes, schRes] = await Promise.all([
                storefrontGet('/config'),
                storefrontGet('/color-schemes'),
            ]);
            setConfig(cfgRes.data?.data ?? cfgRes.data);
            setSchemes(schRes.data?.data ?? schRes.data ?? {});
        } catch (err) {
            console.error(err);
            showNotification('error', 'Impossible de charger la configuration.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateConfig = (path, value) => {
        setConfig(prev => {
            const next = { ...prev };
            const keys = path.split('.');
            let cursor = next;
            for (let i = 0; i < keys.length - 1; i++) {
                cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
                cursor = cursor[keys[i]];
            }
            cursor[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const handleSchemeClick = (key) => {
        if (key === 'custom') {
            updateConfig('color_scheme', 'custom');
        } else {
            const palette = schemes[key] || {};
            setConfig(prev => ({
                ...prev,
                color_scheme: key,
                theme: { ...prev?.theme, ...palette },
            }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        showNotification('loading', 'Enregistrement...');
        try {
            await storefrontPut('/config', config);
            showNotification('success', 'Configuration enregistrée avec succès !');
        } catch (err) {
            console.error(err);
            showNotification('error', err?.response?.data?.message || 'Une erreur est survenue.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20 gap-3 text-sm text-gray-500">
                <RefreshCw size={18} className="animate-spin text-primary" />
                Chargement de la configuration...
            </div>
        );
    }

    if (!config) return null;

    const schemeKeys = Object.keys(SCHEME_LABELS);

    return (
        <div className="px-4 py-6 max-w-3xl mx-auto">
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(n => ({ ...n, show: false }))}
            />

            {/* ── Color Scheme ─────────────────────────────────────────── */}
            <Section title="Palette de couleurs" icon={Palette}>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Choisissez un thème prédéfini ou personnalisez chaque couleur.
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
                    {schemeKeys.map(key => {
                        const palette = schemes[key] || {};
                        const active  = config.color_scheme === key || (!config.color_scheme && key === 'default');
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleSchemeClick(key)}
                                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all text-xs font-medium ${
                                    active
                                        ? 'border-primary bg-primary/5 text-primary'
                                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex gap-1">
                                    {palette.primary_color && <ColorDot color={palette.primary_color} />}
                                    {palette.secondary_color && <ColorDot color={palette.secondary_color} />}
                                    {palette.accent_color && <ColorDot color={palette.accent_color} />}
                                </div>
                                {SCHEME_LABELS[key] || key}
                            </button>
                        );
                    })}
                </div>

                {/* Custom colors */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {[
                        { key: 'theme.primary_color',    label: 'Couleur principale' },
                        { key: 'theme.secondary_color',  label: 'Secondaire' },
                        { key: 'theme.accent_color',     label: 'Accent' },
                        { key: 'theme.background_color', label: 'Arrière-plan' },
                        { key: 'theme.text_color',       label: 'Texte' },
                    ].map(({ key, label }) => {
                        const val = key.split('.').reduce((o, k) => o?.[k], config) || '#000000';
                        return (
                            <label key={key} className="block">
                                <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{label}</span>
                                <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
                                    <input
                                        type="color"
                                        value={val}
                                        onChange={e => {
                                            updateConfig('color_scheme', 'custom');
                                            updateConfig(key, e.target.value);
                                        }}
                                        className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0"
                                    />
                                    <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{val}</span>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </Section>

            {/* ── Typography ───────────────────────────────────────────── */}
            <Section title="Typographie" icon={Type} defaultOpen={false}>
                <FieldRow label="Police principale">
                    <AppleSelect
                        value={config.theme?.font_family || 'Inter'}
                        onChange={e => updateConfig('theme.font_family', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        {['Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Raleway', 'Nunito'].map(f => (
                            <option key={f}>{f}</option>
                        ))}
                    </AppleSelect>
                </FieldRow>
                <FieldRow label="Police des titres">
                    <AppleSelect
                        value={config.theme?.heading_font_family || config.theme?.font_family || 'Inter'}
                        onChange={e => updateConfig('theme.heading_font_family', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        {['Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Raleway', 'Nunito'].map(f => (
                            <option key={f}>{f}</option>
                        ))}
                    </AppleSelect>
                </FieldRow>
                <FieldRow label="Arrondi des bordures">
                    <AppleSelect
                        value={config.theme?.border_radius || '8px'}
                        onChange={e => updateConfig('theme.border_radius', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        {['0px', '4px', '8px', '12px', '16px', '24px'].map(r => (
                            <option key={r}>{r}</option>
                        ))}
                    </AppleSelect>
                </FieldRow>
            </Section>

            {/* ── Infos générales ──────────────────────────────────────── */}
            <Section title="Informations générales" icon={Globe} defaultOpen={false}>
                <FieldRow label="Nom de la boutique">
                    <input
                        type="text"
                        value={config.store_name || ''}
                        onChange={e => updateConfig('store_name', e.target.value)}
                        placeholder="Mon Restaurant"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
                <FieldRow label="Description">
                    <textarea
                        value={config.store_description || ''}
                        onChange={e => updateConfig('store_description', e.target.value)}
                        rows={3}
                        placeholder="Description de votre boutique..."
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary resize-none"
                    />
                </FieldRow>
                <FieldRow label="Devise">
                    <AppleSelect
                        value={config.currency || 'MAD'}
                        onChange={e => updateConfig('currency', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        {['MAD', 'EUR', 'USD', 'GBP', 'TND', 'DZD'].map(c => <option key={c}>{c}</option>)}
                    </AppleSelect>
                </FieldRow>
                <FieldRow label="Langue">
                    <AppleSelect
                        value={config.locale || 'fr'}
                        onChange={e => updateConfig('locale', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        <option value="fr">Français</option>
                        <option value="ar">Arabe</option>
                        <option value="en">Anglais</option>
                    </AppleSelect>
                </FieldRow>
                <FieldRow label="Logo URL">
                    <input
                        type="url"
                        value={config.logo || ''}
                        onChange={e => updateConfig('logo', e.target.value)}
                        placeholder="https://cdn.example.com/logo.png"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
                <FieldRow label="Favicon URL">
                    <input
                        type="url"
                        value={config.favicon || ''}
                        onChange={e => updateConfig('favicon', e.target.value)}
                        placeholder="https://cdn.example.com/favicon.ico"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
            </Section>

            {/* ── SEO ──────────────────────────────────────────────────── */}
            <Section title="SEO" icon={Search} defaultOpen={false}>
                <FieldRow label="Titre meta">
                    <input
                        type="text"
                        value={config.seo?.meta_title || ''}
                        onChange={e => updateConfig('seo.meta_title', e.target.value)}
                        placeholder="Mon Restaurant — Casablanca"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
                <FieldRow label="Description meta">
                    <textarea
                        value={config.seo?.meta_description || ''}
                        onChange={e => updateConfig('seo.meta_description', e.target.value)}
                        rows={3}
                        placeholder="Découvrez notre carte..."
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary resize-none"
                    />
                </FieldRow>
                <FieldRow label="Image OG">
                    <input
                        type="url"
                        value={config.seo?.og_image || ''}
                        onChange={e => updateConfig('seo.og_image', e.target.value)}
                        placeholder="https://cdn.example.com/og.jpg"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
            </Section>

            {/* ── Réseaux sociaux ──────────────────────────────────────── */}
            <Section title="Réseaux sociaux" icon={Share2} defaultOpen={false}>
                {[
                    { key: 'social_links.facebook',  label: 'Facebook',  icon: Facebook, ph: 'https://facebook.com/votreboutique' },
                    { key: 'social_links.instagram', label: 'Instagram', icon: Instagram, ph: 'https://instagram.com/votreboutique' },
                    { key: 'social_links.whatsapp',  label: 'WhatsApp',  icon: Phone,    ph: '+212600000000' },
                ].map(({ key, label, icon: Icon, ph }) => (
                    <FieldRow key={key} label={label}>
                        <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
                            <Icon size={15} className="text-gray-400 flex-shrink-0" />
                            <input
                                type="text"
                                value={key.split('.').reduce((o, k) => o?.[k], config) || ''}
                                onChange={e => updateConfig(key, e.target.value)}
                                placeholder={ph}
                                className="flex-1 text-sm outline-none dark:bg-transparent dark:text-white"
                            />
                        </div>
                    </FieldRow>
                ))}
            </Section>

            {/* ── Layout & affichage ───────────────────────────────────── */}
            <Section title="Layout & affichage" icon={Layout} defaultOpen={false}>
                <FieldRow label="Mode d'affichage">
                    <AppleSelect
                        value={config.layout_mode || 'modern'}
                        onChange={e => updateConfig('layout_mode', e.target.value)}
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    >
                        <option value="modern">Moderne</option>
                        <option value="classic">Classique</option>
                        <option value="minimal">Minimal</option>
                    </AppleSelect>
                </FieldRow>
                {[
                    { key: 'show_search',       label: 'Afficher la recherche' },
                    { key: 'show_cart',         label: 'Afficher le panier' },
                    { key: 'maintenance_mode',  label: 'Mode maintenance' },
                    { key: 'header_config.sticky',      label: 'Header sticky' },
                    { key: 'header_config.show_phone',  label: 'Afficher le téléphone dans le header' },
                    { key: 'footer_config.show_social', label: 'Afficher les réseaux sociaux dans le footer' },
                ].map(({ key, label }) => {
                    const val = !!key.split('.').reduce((o, k) => o?.[k], config);
                    return (
                        <div key={key} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
                            <Toggle checked={val} onChange={v => updateConfig(key, v)} />
                        </div>
                    );
                })}
                <FieldRow label="Copyright footer">
                    <input
                        type="text"
                        value={config.footer_config?.copyright || ''}
                        onChange={e => updateConfig('footer_config.copyright', e.target.value)}
                        placeholder="© 2026 Mon Restaurant"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-800 dark:text-white outline-none focus:border-primary"
                    />
                </FieldRow>
            </Section>

            {/* ── Preview bar ──────────────────────────────────────────── */}
            <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-4 mb-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                <Eye size={16} className="text-primary flex-shrink-0" />
                La prévisualisation en temps réel sera disponible dans une prochaine version.
            </div>

            {/* ── Save ─────────────────────────────────────────────────── */}
            <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isSaving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {isSaving ? 'Enregistrement...' : 'Enregistrer la configuration'}
            </button>
        </div>
    );
};

export default ThemeSettings;
